/**
 * Hook pour les Feature Flags dynamiques par organisation
 * ✅ Migré vers React Query — staleTime 15min (flags très stables)
 */

import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { FEATURE_FLAGS } from '@/config/features';
import { QUERY_KEYS } from '@/constants/queryKeys';

// Liste des feature flags disponibles
export const AVAILABLE_FEATURE_FLAGS = [
  { key: 'teams', label: 'Équipes', description: 'Défis et classements par équipe' },
  { key: 'boosts', label: 'Boosts', description: 'Multiplicateurs XP temporaires' },
  { key: 'streaks', label: 'Streaks', description: 'Séries de jours consécutifs' },
  { key: 'photo_validation', label: 'Validation Photo IA', description: 'Validation des trajets par photo' },
  { key: 'gps_validation', label: 'Validation GPS', description: 'Validation par géolocalisation' },
  { key: 'watch_validation', label: 'Validation Montre', description: 'Validation par smartwatch Terra' },
  // ⚠️ 'shop' n'est lue NULLE PART dans le front (vérifié 12/09/2026) : la bascule
  // ne ferme rien. Ne pas s'en servir pour restreindre une organisation ; la boutique
  // marchande se ferme avec 'partner_offers' ci-dessous.
  { key: 'shop', label: 'Boutique (sans effet)', description: 'Héritage : cette clé n\'est lue par aucun écran, la bascule n\'a aucun effet. Pour fermer la boutique marchande d\'une organisation, utiliser « Boutique marchande » (partner_offers).' },
  { key: 'happy_hours', label: 'Happy Hours', description: 'Bonus XP temporaires organisés' },
  { key: 'social_sharing', label: 'Partage Social', description: 'Partage des accomplissements' },
  { key: 'leaderboard', label: 'Classement', description: 'Tableau des meilleurs scores' },
  { key: 'badges', label: 'Badges', description: 'Système de badges et accomplissements' },
  { key: 'avatars', label: 'Avatars', description: 'Personnalisation des avatars' },
  { key: 'feed', label: 'Fil d\'activité', description: 'Fil des activités de l\'équipe' },
  // === UI Experiments (A/B test per organization) — default off ===
  { key: 'ui_cse_v2', label: 'CSE : Interface V2 (A/B test)', description: 'Active la nouvelle interface CSE "simpliste" pour les utilisateurs de l\'organisation. Par défaut désactivée.' },
  { key: 'ui_rse_v2', label: 'RSE : Interface V2 (A/B test)', description: 'Active la nouvelle interface RSE "simpliste" pour les utilisateurs de l\'organisation. Par défaut désactivée.' },
  { key: 'ui_employee_v2', label: 'Employé : Interface V2 (signature A/B)', description: 'Active la nouvelle interface Employé "simpliste signature" (Home + Dashboard) pour les collaborateurs de l\'organisation. Par défaut désactivée.' },
  { key: 'platform_adaptive_ux', label: 'UX adaptative plateforme (iOS ↔ Android Material)', description: 'Active les tokens Material Design sur Android (drawers, scrims, handles, pull-to-refresh, easing). iOS reste inchangé. Orthogonal aux A/B tests d\'interface. Par défaut désactivée.' },
  // === Provider GPS background pluggable (per-org, default off) ===
  { key: 'custom_gps_plugin', label: 'GPS : Plugin natif custom (pilote graduel)', description: 'false = plugin GPS Median (défaut). true = plugin natif custom Koken (window.kokenLocation). Bascule per-org pour pilote graduel. N\'a d\'effet que sur les devices où le plugin natif custom est réellement installé, sinon Median reste utilisé. Par défaut désactivée.' },
  // === Pronostic Over/Under hebdo (pilote V1, default off) ===
  { key: 'predictions', label: 'Pronostics Over/Under (pilote)', description: 'Pari hebdomadaire sur le total de pas de l\'organisation : carte de vote dans « À la une », +5 XP au vote, +40 XP au camp gagnant (règlement automatique le mardi). Le front exige flags.predictions === true : par défaut désactivée partout.' },
  // === Dons Arbore (pilote per-org, default off) ===
  { key: 'donations', label: 'Dons (Arbore)', description: 'Affiche la categorie Dons dans la boutique : 4 produits Arbore payables en dokens, complement par carte bancaire. La livraison est MANUELLE (V1), n\'ouvrir qu\'a une organisation prete a honorer les dons. Par defaut desactivee partout (FEATURE_FLAGS.DONATIONS_ENABLED = false).' },
  // === Boutique marchande : offres partenaires + achats par carte (per-org, default off) ===
  { key: 'partner_offers', label: 'Boutique marchande (offres partenaires + paiement par carte)', description: 'Gouverne TOUT ce qui peut coûter de l\'argent réel au salarié : offres partenaires (listes, carte, compteurs, achat), récompenses internes marchandes (culture, sport, loisirs, vacances, bien-être et toute autre catégorie hors gamification et dons), paiement par carte bancaire, et la carte « adresses de livraison » du profil. Fermée, il ne reste que la boutique de jeu en diamants (et les dons si « donations » est ouvert). Garde serveur miroir sur les RPC du catalogue, la création de commande, l\'écriture des adresses et les fonctions edge de paiement. Ne pas ouvrir à une organisation dont le contrat l\'exclut. Par défaut désactivée partout (FEATURE_FLAGS.PARTNER_OFFERS_ENABLED = false).' },
  // === Ma semaine — fusion Objectifs + Défis (pilote A/B, default off) ===
  { key: 'ui_ma_semaine_v1', label: 'Ma semaine : fusion Objectifs + Défis (A/B)', description: 'Active la page fusionnée /employee/ma-semaine (défi de la semaine + objectifs auto + temps fort) et fusionne les onglets Défis + Objectifs en un seul « Ma semaine ». Les pages d\'origine restent accessibles. Par défaut désactivée ; rollback = flag off.' },
] as const;

export type FeatureFlagKey = typeof AVAILABLE_FEATURE_FLAGS[number]['key'];

/**
 * Clés déclarées : celles de la liste ci-dessus, plus celles qui portent un
 * défaut global dans FEATURE_FLAGS (`<CLE>_ENABLED`).
 *
 * Une clé DÉCLARÉE garde son défaut historique (`true` quand rien ne dit le
 * contraire) : le basculer casserait teams, leaderboard, happy_hours et les
 * autres pour les 13 organisations sans ligne en base.
 *
 * Une clé NON déclarée — faute de frappe, clé retirée du code, clé inventée par
 * un appelant — valait `true` elle aussi : une fonctionnalité s'ouvrait par
 * accident, ce qui est l'inverse de ce qu'on veut d'un réglage. Elle vaut
 * maintenant `false`, avec un avertissement en console (une fois par clé).
 */
const DECLARED_FEATURE_FLAG_KEYS: ReadonlySet<string> = new Set(
  AVAILABLE_FEATURE_FLAGS.map(f => f.key as string)
);

const warnedUnknownFlagKeys = new Set<string>();

export function isDeclaredFeatureFlagKey(key: string): boolean {
  if (DECLARED_FEATURE_FLAG_KEYS.has(key)) return true;
  return (key.toUpperCase() + '_ENABLED') in FEATURE_FLAGS;
}

/**
 * Résolution commune : ligne en base > défaut global FEATURE_FLAGS > défaut de
 * la clé déclarée (`true`) > `false` pour une clé inconnue.
 */
export function resolveFeatureFlag(key: string, flags: Record<string, boolean>): boolean {
  if (key in flags) return flags[key];

  const globalKey = key.toUpperCase() + '_ENABLED';
  if (globalKey in FEATURE_FLAGS) {
    return FEATURE_FLAGS[globalKey as keyof typeof FEATURE_FLAGS] as boolean;
  }

  if (DECLARED_FEATURE_FLAG_KEYS.has(key)) return true;

  if (!warnedUnknownFlagKeys.has(key)) {
    warnedUnknownFlagKeys.add(key);
    console.warn(
      `[FeatureFlags] Clé inconnue « ${key} » : fermée par défaut. ` +
      'Déclarez-la dans AVAILABLE_FEATURE_FLAGS (ou posez un défaut ' +
      `${globalKey} dans FEATURE_FLAGS) si elle est légitime.`
    );
  }
  return false;
}

interface OrganizationFeatureFlag {
  id: string;
  organization_id: string;
  feature_key: string;
  is_enabled: boolean;
  config: Record<string, unknown>;
  reason: string | null;
  expires_at: string | null;
}

interface UseOrganizationFeatureFlagsResult {
  flags: Record<string, boolean>;
  flagsData: OrganizationFeatureFlag[];
  isLoading: boolean;
  isEnabled: (key: FeatureFlagKey | string) => boolean;
  getConfig: (key: string) => Record<string, unknown> | null;
  refetch: () => Promise<void>;
}

export function useOrganizationFeatureFlags(): UseOrganizationFeatureFlagsResult {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const organizationId = user?.organizationId ?? null;

  const { data: flagsData = [], isLoading } = useQuery({
    queryKey: [QUERY_KEYS.ORG_FEATURE_FLAGS, organizationId],
    queryFn: async (): Promise<OrganizationFeatureFlag[]> => {
      const { data, error } = await supabase
        .from('organization_feature_flags')
        .select('*')
        .eq('organization_id', organizationId!);

      if (error) {
        console.error('[FeatureFlags] Error fetching flags:', error);
        return [];
      }

      const now = new Date();
      return ((data || []) as OrganizationFeatureFlag[]).filter(f => {
        if (!f.expires_at) return true;
        return new Date(f.expires_at) > now;
      });
    },
    enabled: !!organizationId,
    staleTime: 15 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  // Convert to simple boolean map
  const flags = useMemo(() => {
    const map: Record<string, boolean> = {};
    flagsData.forEach(f => {
      map[f.feature_key] = f.is_enabled;
    });
    return map;
  }, [flagsData]);

  // DB flag > Global config > clé déclarée (true) > clé inconnue (false).
  const isEnabled = useCallback(
    (key: FeatureFlagKey | string): boolean => resolveFeatureFlag(key, flags),
    [flags]
  );

  const getConfig = useCallback((key: string): Record<string, unknown> | null => {
    const flag = flagsData.find(f => f.feature_key === key);
    return flag?.config || null;
  }, [flagsData]);

  const refetch = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORG_FEATURE_FLAGS, organizationId] });
  }, [queryClient, organizationId]);

  return {
    flags,
    flagsData,
    isLoading,
    isEnabled,
    getConfig,
    refetch,
  };
}

/**
 * Hook pour l'admin - récupère tous les flags de toutes les organisations
 * ✅ Migré vers React Query
 */
export function useAllOrganizationFeatureFlags() {
  const queryClient = useQueryClient();

  const { data: allFlags = [], isLoading } = useQuery({
    queryKey: [QUERY_KEYS.ALL_ORG_FEATURE_FLAGS],
    queryFn: async (): Promise<OrganizationFeatureFlag[]> => {
      const { data, error } = await supabase
        .from('organization_feature_flags')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('[FeatureFlags] Error fetching all flags:', error);
        return [];
      }

      return (data || []) as OrganizationFeatureFlag[];
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 20 * 60 * 1000,
  });

  const refetch = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ALL_ORG_FEATURE_FLAGS] });
  }, [queryClient]);

  return {
    allFlags,
    isLoading,
    refetch,
  };
}
